import { z } from 'zod';

export const MAX_INSTALLER_BYTES = 512 * 1024 * 1024;
// Fits below typical reverse-proxy limits as well as Cloudflare's per-request cap.
export const INSTALLER_CHUNK_BYTES = 512 * 1024;
export const DESKTOP_PLATFORMS = ['mac-arm64', 'windows-x64', 'linux-x64'] as const;
export type DesktopPlatform = (typeof DESKTOP_PLATFORMS)[number];

const installers = [
  { platform: 'mac-arm64', pattern: /^Lyrmo-(\d+\.\d+\.\d+)-arm64\.dmg$/ },
  { platform: 'windows-x64', pattern: /^Lyrmo-(\d+\.\d+\.\d+)-x64\.exe$/ },
  { platform: 'linux-x64', pattern: /^Lyrmo-(\d+\.\d+\.\d+)(?:-(?:x86_64|x64))?\.AppImage$/ },
] as const;

/** Recognizes only supported installer filenames, without accepting paths. */
export function desktopInstallerDetails(fileName: string): {
  readonly platform: DesktopPlatform;
  readonly version: string;
} | null {
  if (fileName.length > 128 || /\s/.test(fileName)) return null;
  for (const { platform, pattern } of installers) {
    const match = pattern.exec(fileName);
    if (match?.[1]) return { platform, version: match[1] };
  }
  return null;
}

export const DesktopInstallerSchema = z.object({
  platform: z.enum(DESKTOP_PLATFORMS),
  version: z.string(),
  fileName: z.string(),
  size: z.number().int().positive(),
  url: z.string(),
});
export type DesktopInstaller = z.infer<typeof DesktopInstallerSchema>;

export const AdminInstallersSchema = z.object({
  installers: z.array(DesktopInstallerSchema),
  maxBytes: z.number().int().positive(),
});
export type AdminInstallers = z.infer<typeof AdminInstallersSchema>;

export const InstallerUploadSchema = z.object({
  uploadId: z.uuid(),
  chunkBytes: z.number().int().positive().max(INSTALLER_CHUNK_BYTES),
});

export const InstallerUploadProgressSchema = z.object({
  receivedBytes: z.number().int().nonnegative(),
});
